import { useCallback, useState } from 'react';

import { MessageInstance } from 'antd/es/message/interface';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import i18n from 'i18next';

import {
  AggregateSectionData,
  TurnCountByTaskData,
} from 'scenes/Kit/Dashboard/types';

import { ApiErrorData } from 'models/ApiResponse';

import { KitCampaignService } from 'services/KitCampaignService';

import { WeekDay } from '../components/WeekDayPicker';
import {
  DashboardKitAlternativeModeData,
  TurnCheckInStatusAttendanceRowData,
  TurnCheckinStatusByDayData,
} from '../types';

dayjs.extend(isoWeek);

const BASE = 'scenes.kit.dashboard_alternative_mode.hooks.use_dashboard_data';

interface UseDashboardDataParams {
  message: MessageInstance;
}

export interface UseDashboardDataReturn {
  aggregateSection?: AggregateSectionData;
  turnCountByTask?: TurnCountByTaskData[];
  turnSummaryStatusGrid?: TurnCheckInStatusAttendanceRowData[];
  turnProgressByDay?: TurnCheckinStatusByDayData;
  dataLoading: boolean;
  fetchDashboardData: (
    dateRange: [Dayjs, Dayjs],
    campaignIds: number[],
    weekDays?: WeekDay[]
  ) => Promise<void>;
}

export function useDashboardData({
  message,
}: UseDashboardDataParams): UseDashboardDataReturn {
  const [dashboardData, setDashboardData] = useState<
    DashboardKitAlternativeModeData | undefined
  >(undefined);
  const [dataLoading, setDataLoading] = useState(false);

  const fetchDashboardData = useCallback(
    async (
      dateRange: [Dayjs, Dayjs],
      campaignIds: number[],
      weekDays?: WeekDay[]
    ) => {
      setDataLoading(true);

      const response =
        await KitCampaignService.getDashboardKitAlternativeModeData({
          startDate: dateRange[0].format('YYYY-MM-DD'),
          endDate: dateRange[1].format('YYYY-MM-DD'),
          taskIds: campaignIds.length > 0 ? campaignIds : undefined,
          weekDays: weekDays && weekDays.length > 0 ? weekDays : undefined,
        });

      if (response.success) {
        const raw = response.data as DashboardKitAlternativeModeData;

        // Filtrar turnCheckinStatusByDay según los días de la semana seleccionados
        if (weekDays && weekDays.length > 0) {
          const weekDaySet = new Set<number>(weekDays);
          const filtered: TurnCheckinStatusByDayData = {};
          for (const dateKey of Object.keys(raw.turnCheckinStatusByDay)) {
            if (weekDaySet.has(dayjs(dateKey).isoWeekday())) {
              filtered[dateKey] = raw.turnCheckinStatusByDay[dateKey];
            }
          }
          raw.turnCheckinStatusByDay = filtered;
        }

        setDashboardData(raw);
      } else {
        const error = response.data as ApiErrorData;
        message.error(
          (error.message as string) || i18n.t(`${BASE}.errors.fetch_failed`),
          3.5
        );
        setDashboardData(undefined);
      }

      setDataLoading(false);
    },
    [message]
  );

  return {
    aggregateSection: dashboardData?.aggregateSection,
    turnCountByTask: dashboardData?.turnCountByTask,
    turnSummaryStatusGrid: dashboardData?.turnCheckinStatusAttendanceRow,
    turnProgressByDay: dashboardData?.turnCheckinStatusByDay,
    dataLoading,
    fetchDashboardData,
  };
}
